import { useEffect, useState } from "react";
import { APP_STORE_URL, apiUrl } from "../../config";
import { useApp } from "../../context/AppContext";
import { signedPost, signedGet } from "../../api/client";
import {
  birthdayRemindersEnabled,
  setBirthdayRemindersEnabled,
} from "../../storage/session";
import { scheduleBirthdayReminders, requestNotificationPermission } from "../../utils/notifications";
import {
  downloadLinkMessage,
  familyInviteMessage,
  additionalDeviceMessage,
  guestInviteMessage,
  shareText,
} from "../../utils/share";
import { ModalSheet } from "../shared/ModalSheet";
import { Avatar } from "../shared/Avatar";
import { EmptyState } from "../shared/LoadingState";

type InviteStep = "hub" | "family" | "ready";

export function SettingsSheet() {
  const { showSettings, setShowSettings, session, me, newsFeed, signOut, refreshAll, upcomingEvents, setShowFeedback } = useApp();
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [talkingPoint, setTalkingPoint] = useState("");
  const [reminders, setReminders] = useState(birthdayRemindersEnabled());
  const [message, setMessage] = useState<string | null>(null);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [showTalkingPoint, setShowTalkingPoint] = useState(false);

  useEffect(() => {
    if (me) {
      setCity(me.location_city ?? "");
      setState(me.location_state ?? "");
    }
  }, [me]);

  if (!showSettings || !session) return null;

  const saveLocation = async () => {
    const canonical = `location|${city.trim()}|${state.trim()}`;
    await signedPost(session, "/family/submissions/location", {
      location_city: city.trim(),
      location_state: state.trim(),
    }, canonical);
    setMessage("Location updated.");
    await refreshAll();
    setShowLocation(false);
  };

  const saveTalkingPoint = async () => {
    const canonical = `talking_point|${talkingPoint.trim()}`;
    await signedPost(session, "/family/submissions/talking-point", {
      text: talkingPoint.trim(),
    }, canonical);
    setTalkingPoint("");
    setMessage("Talking point saved.");
    await refreshAll();
    setShowTalkingPoint(false);
  };

  const existingTalkingPoint = newsFeed?.talking_points.find(
    (t) => t.person_id === session.person_id,
  );
  const override = newsFeed?.location_overrides.find((o) => o.person_id === session.person_id);
  const locationLine = override
    ? `${override.location_city}${override.location_state ? `, ${override.location_state}` : ""}`
    : [me?.location_city, me?.location_state].filter(Boolean).join(", ");

  return (
    <ModalSheet open={showSettings} onClose={() => setShowSettings(false)} title="Your settings">
      {me ? (
        <div className="settings-profile">
          <Avatar person={me} size={72} ring />
          <div>
            <strong>{me.display_name}</strong>
            {locationLine ? <div className="muted">{locationLine}</div> : null}
          </div>
        </div>
      ) : null}
      {message ? <p className="muted">{message}</p> : null}

      <button type="button" className="settings-row" onClick={() => setShowLocation(true)}>
        Update location
      </button>
      <button type="button" className="settings-row" onClick={() => setShowTalkingPoint(true)}>
        Edit talking points
      </button>
      <button type="button" className="settings-row" onClick={() => { setShowSettings(false); }}>
        Change photo (via feedback)
      </button>

      <label className="settings-toggle">
        <span>Birthday reminders</span>
        <input
          type="checkbox"
          checked={reminders}
          onChange={(e) => {
            setReminders(e.target.checked);
            setBirthdayRemindersEnabled(e.target.checked);
            if (e.target.checked) {
              void requestNotificationPermission().then(() => scheduleBirthdayReminders(upcomingEvents));
            }
          }}
        />
      </label>

      <a href={APP_STORE_URL} className="settings-row linkish">Send the app</a>
      <button type="button" className="settings-row" onClick={() => { setShowSettings(false); setShowFeedback(true); }}>
        Report an issue
      </button>

      <details className="settings-faq">
        <summary>Profile FAQ</summary>
        <p className="muted">Location and talking points update your profile for the whole family. Events and photo changes go through Kyle for review.</p>
      </details>

      <p className="muted copyright">© 2026 Kyle Auger</p>

      {!confirmSignOut ? (
        <button type="button" className="btn-ghost destructive" onClick={() => setConfirmSignOut(true)}>
          Remove from this device
        </button>
      ) : (
        <div className="stack">
          <p>Sign out and remove this device&apos;s access?</p>
          <button type="button" className="btn-primary" onClick={() => void signOut()}>
            Yes, remove
          </button>
          <button type="button" className="btn-ghost" onClick={() => setConfirmSignOut(false)}>
            Cancel
          </button>
        </div>
      )}

      <ModalSheet open={showLocation} onClose={() => setShowLocation(false)} title="Update location">
        <input className="field" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
        <input className="field" placeholder="State" value={state} onChange={(e) => setState(e.target.value)} />
        <button type="button" className="btn-primary" onClick={() => void saveLocation()}>
          Save
        </button>
      </ModalSheet>

      <ModalSheet open={showTalkingPoint} onClose={() => setShowTalkingPoint(false)} title="Talking point">
        {existingTalkingPoint ? <p className="muted">Current: {existingTalkingPoint.text}</p> : null}
        <textarea className="field" rows={3} maxLength={280} value={talkingPoint} onChange={(e) => setTalkingPoint(e.target.value)} />
        <button type="button" className="btn-primary" disabled={!talkingPoint.trim()} onClick={() => void saveTalkingPoint()}>
          Save
        </button>
      </ModalSheet>
    </ModalSheet>
  );
}

export function InvitesSheet() {
  const { showInvites, setShowInvites, session } = useApp();
  const [step, setStep] = useState<InviteStep>("hub");
  const [recipients, setRecipients] = useState<Array<{ person_id: string; display_name: string }>>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  if (!showInvites || !session) return null;

  const webUrl = `${window.location.origin}${apiUrl("/app/")}`;

  const loadRecipients = async () => {
    const eligible = await signedGet<{ recipients: Array<{ person_id: string; display_name: string }> }>(
      session,
      "/family/invite-eligible",
      "",
    );
    setRecipients(eligible.recipients);
    setStep("family");
  };

  const createInvite = async (kind: "family_member" | "additional_device" | "unlisted", recipientId?: string) => {
    if (kind === "family_member" && recipientId) {
      const canonical = `family_member|${recipientId}`;
      const res = await signedPost<{ invite_code: string; recipient_display_name?: string }>(
        session,
        "/family/invites",
        { kind: "family_member", recipient_person_id: recipientId },
        canonical,
      );
      setCode(res.invite_code);
      setMessage(`Invite for ${res.recipient_display_name ?? "family member"} ready.`);
      setStep("ready");
      return;
    }
    if (kind === "additional_device") {
      const res = await signedPost<{ invite_code: string }>(
        session,
        "/family/invites",
        { kind: "additional_device" },
        "additional_device",
      );
      setCode(res.invite_code);
      setMessage("Additional device code created.");
      setStep("ready");
      return;
    }
    const res = await signedPost<{ invite_code: string }>(
      session,
      "/family/invites",
      {
        kind: "unlisted",
        first_name: "Guest",
        last_name: "Family",
        relationship_note: "Web guest invite",
      },
      "unlisted|Guest|Family|Web guest invite",
    );
    setCode(res.invite_code);
    setMessage("Guest code created.");
    setStep("ready");
  };

  const sharePayload = () => {
    if (!code) return downloadLinkMessage(webUrl);
    if (step === "ready" && selectedRecipient) {
      const name = recipients.find((r) => r.person_id === selectedRecipient)?.display_name ?? "there";
      return familyInviteMessage(webUrl, name, code);
    }
    if (message?.includes("device")) return additionalDeviceMessage(webUrl, code);
    if (message?.includes("Guest")) return guestInviteMessage(webUrl, code);
    return downloadLinkMessage(webUrl) + `\n\nYour code: ${code}`;
  };

  const share = async () => {
    const result = await shareText("Sundius Family", sharePayload());
    setMessage(result === "copied" ? "Copied share message." : "Shared!");
  };

  const filtered = recipients.filter((r) =>
    r.display_name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <ModalSheet
      open={showInvites}
      onClose={() => { setShowInvites(false); setStep("hub"); setCode(null); }}
      title="Who needs it?"
    >
      {message ? <p className="muted">{message}</p> : null}
      {code ? <pre className="invite-code">{code}</pre> : null}

      {step === "hub" ? (
        <>
          <button type="button" className="btn-primary" onClick={() => void shareText("Sundius Family", downloadLinkMessage(webUrl)).then(() => setMessage("Download link shared."))}>
            Just the download link
          </button>
          <button type="button" className="btn-primary" onClick={() => void loadRecipients()}>
            Someone in the family
          </button>
          <button type="button" className="btn-primary" onClick={() => void createInvite("additional_device")}>
            My other phone or iPad
          </button>
          <button type="button" className="btn-primary" onClick={() => void createInvite("unlisted")}>
            Someone new (guest)
          </button>
        </>
      ) : null}

      {step === "family" ? (
        <>
          <input className="field" placeholder="Search family" value={search} onChange={(e) => setSearch(e.target.value)} />
          {filtered.length === 0 ? (
            <EmptyState message="Everyone in the family already has the app." />
          ) : (
            filtered.map((r) => (
              <button
                key={r.person_id}
                type="button"
                className={`choice-btn ${selectedRecipient === r.person_id ? "selected" : ""}`}
                onClick={() => setSelectedRecipient(r.person_id)}
              >
                {r.display_name}
              </button>
            ))
          )}
          <button
            type="button"
            className="btn-primary"
            disabled={!selectedRecipient}
            onClick={() => selectedRecipient && void createInvite("family_member", selectedRecipient)}
          >
            Create invite
          </button>
          <button type="button" className="btn-ghost" onClick={() => setStep("hub")}>
            Back
          </button>
        </>
      ) : null}

      {step === "ready" && code ? (
        <button type="button" className="btn-primary" onClick={() => void share()}>
          Share message
        </button>
      ) : null}
    </ModalSheet>
  );
}

export function FeedbackSheet() {
  const { showFeedback, setShowFeedback, session } = useApp();
  const [category, setCategory] = useState("correction");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  if (!showFeedback || !session) return null;

  const submit = async () => {
    const canonical = `feedback|${category}|${message.trim()}`;
    await signedPost(session, "/family/feedback", {
      category,
      message: message.trim(),
    }, canonical);
    setStatus("Thanks — Kyle will see this.");
    setMessage("");
  };

  return (
    <ModalSheet open={showFeedback} onClose={() => setShowFeedback(false)} title="Share feedback">
      <select className="field" value={category} onChange={(e) => setCategory(e.target.value)}>
        <option value="correction">Correction</option>
        <option value="photo_change">Photo change</option>
        <option value="feature_idea">Feature idea</option>
        <option value="other">Other</option>
      </select>
      <textarea className="field" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />
      {status ? <p className="muted">{status}</p> : null}
      <button type="button" className="btn-primary" disabled={!message.trim()} onClick={() => void submit()}>
        Send
      </button>
    </ModalSheet>
  );
}

export function FeedbackReplyModal() {
  const { feedbackReplies, session, refreshAll } = useApp();
  const [queue, setQueue] = useState(feedbackReplies);
  const reply = queue[0];

  useEffect(() => {
    setQueue(feedbackReplies);
  }, [feedbackReplies]);

  if (!reply || !session) return null;

  const markRead = async () => {
    const canonical = `feedback_reply_read|${reply.id}`;
    await signedPost(
      session,
      `/family/feedback/replies/${encodeURIComponent(reply.id)}/read`,
      {},
      canonical,
    );
    setQueue((prev) => prev.slice(1));
    if (queue.length <= 1) await refreshAll();
  };

  return (
    <ModalSheet open dismissOnBackdrop={false} onClose={() => {}} title="Reply from Kyle">
      <p>{reply.message}</p>
      <button type="button" className="btn-primary" onClick={() => void markRead()}>
        Got it
      </button>
    </ModalSheet>
  );
}
